import { createContext, useContext, ReactNode, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useUserStore } from '@/store/userStore';
import { signOutLocal } from '@/services/auth/sessionManager';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { toast } from 'sonner';
import i18n from '@/i18n';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  gymId: string | null;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  gymId: null,
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useSupabaseAuth();
  const { setUser, setSession } = useUserStore();
  
  // Add additional logging
  useEffect(() => {
    // Synchronize auth state with userStore
    setUser(auth.user);
    setSession(auth.session);
  }, [auth.user, auth.loading, auth.session, setUser, setSession]);
  
  
  usePushNotifications(auth.user?.id ?? null);
  
  
  const logout = async () => {
    try {
      // Révocation globale de la session serveur
      // Déconnexion locale : les autres appareils restent connectés.
      await signOutLocal();
      toast.success(i18n.t("common:toasts.logoutSuccess"));
    } catch (error) {
      console.error('Unexpected logout error:', error);
    } finally {
      // Toujours nettoyer le store et rediriger
      useUserStore.getState().clearAuth();
      
      // Nettoyer le hash éventuel (#access_token) PUIS rediriger dans tous
      // les cas. Avec l'ancien code, un hash non vide (retour de magic link)
      // ne déclenchait qu'un `replaceState` : l'URL affichait `/login` mais la
      // page applicative restait montée à l'écran, avec ses données.
      if (window.location.hash) {
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );
      }
      window.location.replace('/login');
    }
  };

  return <AuthContext.Provider value={{...auth, logout}}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
